import { getVerifyOrigin, getCookieDomainFromHostname } from "utils/environment";
import { getCookie, setCookie } from "utils/cookie";

const LABEL_STORAGE_PREFIX = "digitalComplianceLabelContext:";
const LABEL_COOKIE_PREFIX = "dcl_ctx_";
const LABEL_CONTEXT_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

function sanitizeTokenPart(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function makePseudoHash(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

export function buildLabelId(sku, productId) {
  const normalized = String(sku || "")
    .trim()
    .replace(/[^a-zA-Z0-9]/g, "");

  if (normalized) {
    return `DCL-${normalized}`;
  }

  const idPart = String(productId || "")
    .trim()
    .replace(/[^a-zA-Z0-9]/g, "");

  if (idPart) {
    return `DCL-${idPart}`;
  }

  return "DCL-00000000";
}

const COUNTRY_CODE_MAP = {
  France: "FR",
  "United Kingdom": "UK",
  Germany: "DE",
  Greece: "GR",
};

export function getCountryCode(countryName) {
  const normalized = String(countryName || "").trim();
  if (!normalized) {
    return "";
  }

  if (COUNTRY_CODE_MAP[normalized]) {
    return COUNTRY_CODE_MAP[normalized];
  }

  return normalized.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase();
}

const COUNTRY_CODE_BY_KEY = {
  uk: "UK",
  fr: "FR",
  gr: "GR",
  it: "IT",
  de: "DE",
};

export function getCountryCodeByKey(countryKey) {
  const normalized = String(countryKey || "").trim().toLowerCase();
  if (COUNTRY_CODE_BY_KEY[normalized]) {
    return COUNTRY_CODE_BY_KEY[normalized];
  }
  return normalized.slice(0, 2).toUpperCase();
}

function normalizeStatus(status) {
  return String(status || "active").trim().toLowerCase();
}

const DEFAULT_DEMO_META = {
  verifiedOn: "27 May 2026",
  lastUpdated: "27 May 2026",
  applicableMarkets: "EU / EEA / UK",
};

function isPpwrCategory(product) {
  return (
    product?.category === "ppwr" ||
    (product?.ppwr && typeof product.ppwr === "object")
  );
}

function normalizeResolvedProduct(product) {
  const isPpwr = isPpwrCategory(product);
  const name =
    product.description?.trim() ||
    product.name?.trim() ||
    product.product?.trim() ||
    (isPpwr ? "PPWR packaging label" : "Authorized product");

  const normalized = {
    ...product,
    name,
    description: product.description?.trim() || name,
    sku: product.sku?.trim() || "—",
    status: normalizeStatus(product.status),
    category: isPpwr ? "ppwr" : product.category || "general",
  };

  if (isPpwr) {
    const generatedOn = product.ppwr?.meta?.generatedOn || "";
    normalized.labelId = product.labelId?.trim() || "";
    normalized.verifiedOn = product.verifiedOn || generatedOn || "";
    normalized.lastUpdated =
      product.lastUpdated ||
      product.updated_at ||
      product.updatedAt ||
      generatedOn ||
      "";
    normalized.applicableMarkets = product.applicableMarkets || "";
    if (product.ppwr && typeof product.ppwr === "object") {
      normalized.ppwr = product.ppwr;
    }
    if (product.ppwrAssets && typeof product.ppwrAssets === "object") {
      normalized.ppwrAssets = product.ppwrAssets;
    }
    return normalized;
  }

  normalized.labelId =
    product.labelId?.trim() || buildLabelId(product.sku, product.id);
  normalized.verifiedOn = product.verifiedOn || DEFAULT_DEMO_META.verifiedOn;
  normalized.lastUpdated =
    product.lastUpdated ||
    product.updated_at ||
    product.updatedAt ||
    DEFAULT_DEMO_META.lastUpdated;
  normalized.applicableMarkets =
    product.applicableMarkets || DEFAULT_DEMO_META.applicableMarkets;

  return normalized;
}

function getLabelContextCookieName(labelToken) {
  return `${LABEL_COOKIE_PREFIX}${labelToken}`;
}

function readLabelContextPayload(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function toCookieSafeProduct(normalized) {
  if (!normalized) return normalized;
  const { ppwrAssets, ...rest } = normalized;
  return rest;
}

function writeLabelContextToCookie(labelToken, normalized) {
  if (!labelToken || !normalized) return;

  const payload = JSON.stringify(toCookieSafeProduct(normalized));
  if (payload.length > 3500) {
    return;
  }

  const cookieOptions = {
    path: "/",
    expires: LABEL_CONTEXT_MAX_AGE_SECONDS,
  };
  const domain = getCookieDomainFromHostname();
  if (domain) {
    cookieOptions.domain = domain;
  }

  setCookie(getLabelContextCookieName(labelToken), payload, cookieOptions);
}

export function buildDigitalLabelToken(product) {
  const skuPart = sanitizeTokenPart(product?.sku || "product");
  const idPart = sanitizeTokenPart(product?.id || "0");
  const statusPart = sanitizeTokenPart(product?.status || "active");
  const hash = makePseudoHash(`${skuPart}:${idPart}:${statusPart}`);
  return `dcl_${skuPart}${hash}`.slice(0, 28);
}

export function getDigitalLabelRouteFromToken(labelToken) {
  return `/digital-compliance-label/${labelToken}`;
}

export function buildDigitalLabelUrl(labelToken) {
  const origin = getVerifyOrigin();
  return `${origin}${getDigitalLabelRouteFromToken(labelToken)}`;
}

function pickIdentityFields(productData = {}) {
  return {
    id: String(productData.id || ""),
    name:
      productData.description?.trim() ||
      productData.name?.trim() ||
      productData.product?.trim() ||
      "Authorized product",
    description:
      productData.description?.trim() ||
      productData.name?.trim() ||
      productData.product?.trim() ||
      "",
    sku: productData.sku?.trim() || "—",
    status: productData.status,
    labelId: productData.labelId?.trim() || "",
    activeFrom: productData.activeFrom || productData.active_from || null,
    activeTill: productData.activeTill || productData.active_to || null,
    comments:
      productData.comments?.trim() || productData.comment?.trim() || "",
    category: productData.category || "general",
    updated_at: productData.updated_at || productData.updatedAt || null,
    updatedAt: productData.updated_at || productData.updatedAt || null,
    verifiedOn: productData.verifiedOn || productData.verified_on || null,
    lastUpdated:
      productData.lastUpdated ||
      productData.updated_at ||
      productData.updatedAt ||
      null,
    applicableMarkets:
      productData.applicableMarkets ||
      productData.applicable_markets ||
      null,
    ppwr: productData.ppwr,
    ppwrAssets: productData.ppwrAssets,
  };
}

export function cacheDigitalLabelContext(labelToken, productData) {
  if (!labelToken || !productData) return;
  const normalized = normalizeResolvedProduct(pickIdentityFields(productData));

  try {
    localStorage.setItem(
      `${LABEL_STORAGE_PREFIX}${labelToken}`,
      JSON.stringify(normalized)
    );
  } catch {
    // Quota exceeded (e.g. large data URLs) — still try cookie for text fields.
    try {
      localStorage.setItem(
        `${LABEL_STORAGE_PREFIX}${labelToken}`,
        JSON.stringify(toCookieSafeProduct(normalized))
      );
    } catch {
      // ignore storage failures in mock flow
    }
  }

  writeLabelContextToCookie(labelToken, normalized);
}

function getCachedDigitalLabelContext(labelToken) {
  if (!labelToken) return null;

  const fromCookie = readLabelContextPayload(
    getCookie(getLabelContextCookieName(labelToken))
  );
  const fromStorage = readLabelContextPayload(
    localStorage.getItem(`${LABEL_STORAGE_PREFIX}${labelToken}`)
  );

  if (fromStorage && fromCookie) {
    return {
      ...fromCookie,
      ...fromStorage,
      ppwr: fromStorage.ppwr || fromCookie.ppwr,
      ppwrAssets: fromStorage.ppwrAssets || fromCookie.ppwrAssets,
    };
  }

  return fromStorage || fromCookie;
}

export function resolveLabelProduct(labelToken, stateProduct) {
  const token = String(labelToken || "");

  if (stateProduct) {
    return normalizeResolvedProduct(pickIdentityFields(stateProduct));
  }

  const cached = getCachedDigitalLabelContext(token);
  if (cached) {
    return normalizeResolvedProduct(cached);
  }

  return normalizeResolvedProduct({
    id: "",
    name: "Authorized product",
    sku: "—",
    status: "active",
  });
}

export async function copyLabelId(labelId) {
  if (!labelId) return false;

  try {
    await navigator.clipboard.writeText(labelId);
    return true;
  } catch {
    return false;
  }
}

export function normalizeCountryCode(value) {
  const raw = String(value ?? "").trim().toUpperCase();
  if (!raw || raw === "—") return "";
  if (raw === "UK") return "GB";
  return raw;
}

export function countryCodesMatch(left, right) {
  const a = normalizeCountryCode(left);
  const b = normalizeCountryCode(right);
  if (!a || !b) return false;
  return a === b;
}

function extractRegionFromLocale(locale) {
  const raw = String(locale ?? "").trim();
  if (!raw) return null;

  try {
    if (typeof Intl !== "undefined" && typeof Intl.Locale === "function") {
      const region = new Intl.Locale(raw).region;
      if (region) return normalizeCountryCode(region);
    }
  } catch {
  }

  const parts = raw.replace(/_/g, "-").split("-").filter(Boolean);
  for (let i = 1; i < parts.length; i += 1) {
    const part = parts[i];
    if (/^[A-Za-z]{2}$/.test(part)) {
      return normalizeCountryCode(part);
    }
  }
  return null;
}

export function extractBrowserRegionCodes() {
  if (typeof navigator === "undefined") return [];

  const locales = [];
  if (Array.isArray(navigator.languages)) {
    locales.push(...navigator.languages);
  }
  if (navigator.language) {
    locales.push(navigator.language);
  }

  const regions = [];
  const seen = new Set();
  for (const locale of locales) {
    const region = extractRegionFromLocale(locale);
    if (!region || seen.has(region)) continue;
    seen.add(region);
    regions.push(region);
  }
  return regions;
}

const TIMEZONE_COUNTRY_MAP = {
  "Europe/Kyiv": "UA",
  "Europe/Kiev": "UA",
  "Europe/London": "GB",
  "Europe/Berlin": "DE",
  "Europe/Paris": "FR",
  "Europe/Rome": "IT",
  "Europe/Madrid": "ES",
  "Europe/Athens": "GR",
  "Europe/Dublin": "IE",
  "Europe/Amsterdam": "NL",
  "Europe/Brussels": "BE",
  "Europe/Vienna": "AT",
  "Europe/Warsaw": "PL",
  "Europe/Prague": "CZ",
  "Europe/Stockholm": "SE",
  "Europe/Helsinki": "FI",
  "Europe/Copenhagen": "DK",
  "Europe/Lisbon": "PT",
  "Europe/Bucharest": "RO",
  "Europe/Sofia": "BG",
  "Europe/Zagreb": "HR",
  "Europe/Budapest": "HU",
  "Europe/Zurich": "CH",
  "Europe/Oslo": "NO",
  "Europe/Tallinn": "EE",
  "Europe/Riga": "LV",
  "Europe/Vilnius": "LT",
  "Europe/Luxembourg": "LU",
  "Europe/Malta": "MT",
  "Europe/Nicosia": "CY",
  "Atlantic/Reykjavik": "IS",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Phoenix": "US",
  "America/Anchorage": "US",
  "America/Honolulu": "US",
  "America/Detroit": "US",
  "America/Indiana/Indianapolis": "US",
  "America/Kentucky/Louisville": "US",
  "America/Boise": "US",
  "America/Juneau": "US",
  "Pacific/Honolulu": "US",
};

export function resolveBrowserTimezoneCountry() {
  let timeZone = "";
  try {
    if (typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function") {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    }
  } catch {
    timeZone = "";
  }

  const normalizedZone = String(timeZone || "").trim();
  if (!normalizedZone) return null;

  const mapped = TIMEZONE_COUNTRY_MAP[normalizedZone];
  if (!mapped) return null;
  return normalizeCountryCode(mapped);
}

export function resolvePpwrTargetMarketCodes(product) {
  const raw = product?.ppwr?.traceability?.targetMarket;
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        const text = String(entry ?? "").trim();
        if (!text) return "";
        if (/^[A-Za-z]{2}$/.test(text) || text.toUpperCase() === "EU") {
          return normalizeCountryCode(text);
        }
        return normalizeCountryCode(getCountryCode(text) || text);
      })
      .filter(Boolean);
  }

  const text = String(raw ?? "").trim();
  if (!text || text === "—") return [];

  return text
    .split(/[,;/|]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      if (/^[A-Za-z]{2}$/.test(part) || part.toUpperCase() === "EU") {
        return normalizeCountryCode(part);
      }
      return normalizeCountryCode(getCountryCode(part) || part);
    })
    .filter(Boolean);
}

export function flagEmojiFromCountryCode(code) {
  const normalized = normalizeCountryCode(code);
  if (!normalized) return "🏳️";
  if (normalized === "EU") return "🇪🇺";
  const iso = normalized.length === 2 ? normalized : "";
  if (!iso) return "🏳️";
  const base = 0x1f1e6;
  return String.fromCodePoint(
    ...[...iso].map((char) => base + char.charCodeAt(0) - 65)
  );
}

export function getCountryDisplayName(code, locale = "en") {
  const normalized = normalizeCountryCode(code);
  if (!normalized) return "";
  if (normalized === "EU") return "European Union";
  if (normalized === "GB") return "United Kingdom";

  try {
    if (typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function") {
      const name = new Intl.DisplayNames([locale], { type: "region" }).of(
        normalized
      );
      if (name) return name;
    }
  } catch {
  }

  return normalized;
}

export function buildCountryOptionFromCode(code, locale = "en") {
  const normalized = normalizeCountryCode(code);
  if (!normalized) return null;
  return {
    key: normalized,
    code: normalized,
    flag: flagEmojiFromCountryCode(normalized),
    name: getCountryDisplayName(normalized, locale),
  };
}

export function resolvePpwrLocationSelection(product) {
  const browserRegions = extractBrowserRegionCodes();
  const timezoneCountry = resolveBrowserTimezoneCountry();
  const targetCodes = resolvePpwrTargetMarketCodes(product);

  const timezoneMatchesTarget = Boolean(
    timezoneCountry &&
      targetCodes.some((target) => countryCodesMatch(timezoneCountry, target))
  );
  const localeMatchingTarget = browserRegions.find((region) =>
    targetCodes.some((target) => countryCodesMatch(region, target))
  );

  let selectedCode = null;
  if (timezoneMatchesTarget) {
    selectedCode = timezoneCountry;
  } else if (localeMatchingTarget) {
    selectedCode = localeMatchingTarget;
  } else if (timezoneCountry) {
    selectedCode = timezoneCountry;
  } else if (browserRegions.length > 0) {
    selectedCode = browserRegions[0];
  }

  if (!selectedCode) {
    return {
      useDemoFallback: true,
      fallback: "DEMO_DETECTED_LOCATION",
      detected: true,
      browserRegions,
      timezoneCountry: null,
      selectedCode: null,
      countries: [],
      matchedTargetMarket: false,
    };
  }

  const countries = [];
  const seen = new Set();
  if (timezoneCountry) {
    countries.push(timezoneCountry);
    seen.add(timezoneCountry);
  }
  for (const region of browserRegions) {
    if (seen.has(region)) continue;
    seen.add(region);
    countries.push(region);
  }

  return {
    useDemoFallback: false,
    fallback: null,
    detected: true,
    browserRegions,
    timezoneCountry,
    selectedCode,
    countries,
    matchedTargetMarket:
      timezoneMatchesTarget || Boolean(localeMatchingTarget),
  };
}
